import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Input, Select, Space, Tag, Tooltip,
  Popconfirm, message, Card, Row, Col, Statistic
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined, RedoOutlined
} from "@ant-design/icons";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { useEventBus, EventName } from "@/hooks/useEventBus";
import moment from "moment";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";

const { Option } = Select;

const STATUS_COLOR = {
  Open: "blue",
  "In Progress": "orange",
  Completed: "green",
  Cancelled: "default",
};

const WorkorderWrapper = () => {
  const navigate = useNavigate();
  const facilityId = useSelector((state) => state.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const detailPath = (id) => buildFacilityPath(`workorder-wrapper-detail/${id}`, facilityContext) || "/switch-view";
  const createPath = buildFacilityPath("create-workorder-wrapper", facilityContext) || "/switch-view";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [ordering, setOrdering] = useState("-created_date");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (facilityId) params.append("facility", facilityId);
      if (searchText) params.append("search", searchText);
      if (statusFilter) params.append("status", statusFilter);
      if (ordering) params.append("ordering", ordering);
      const res = await api.filterWorkorderWrapper(params.toString());
      if (res.status === 200) {
        setData(res.data.results || []);
        setTotal(res.data.count || 0);
      }
    } catch {
      message.error("Failed to load Workorder Wrappers");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, facilityId, searchText, statusFilter, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEventBus(EventName.REFRESH_WORKORDER_WRAPPER_LIST, fetchData);

  const handleDelete = async (id) => {
    try {
      await api.deleteWorkorderWrapper(id);
      message.success("Deleted successfully");
      fetchData();
    } catch {
      message.error("Failed to delete");
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: true,
      sortOrder: ordering === "title" ? "ascend" : ordering === "-title" ? "descend" : null,
      render: (text, record) => (
        <a onClick={() => navigate(detailPath(record.id))}>{text}</a>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (v) => <Tag color={STATUS_COLOR[v] || "default"}>{v}</Tag>,
    },
    {
      title: "PM Plan",
      dataIndex: "pm_plan_name",
      key: "pm_plan_name",
      render: (v) => v ? <Tag color="purple">{v}</Tag> : <span style={{ color: "#aaa" }}>Manual</span>,
    },
    {
      title: "Workorders",
      dataIndex: "workorders_count",
      key: "workorders_count",
      width: 100,
      align: "center",
      render: (v) => <Tag color="blue">{v || 0}</Tag>,
    },
    {
      title: "Total Cost",
      dataIndex: "total_cost",
      key: "total_cost",
      width: 120,
      render: (v) => v ? `$${parseFloat(v).toFixed(2)}` : "-",
    },
    {
      title: "Budget",
      dataIndex: "estimate_budget",
      key: "estimate_budget",
      width: 120,
      render: (v) => v ? `$${parseFloat(v).toFixed(2)}` : "-",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      width: 110,
      render: (v) => v || "-",
    },
    {
      title: "Created",
      dataIndex: "created_date",
      key: "created_date",
      width: 110,
      sorter: true,
      sortOrder: ordering === "created_date" ? "ascend" : ordering === "-created_date" ? "descend" : null,
      render: (v) => v ? moment(v).format("YYYY-MM-DD") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Detail">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(detailPath(record.id))}
            />
          </Tooltip>
          <Popconfirm title="Delete this workorder wrapper?" onConfirm={() => handleDelete(record.id)}>
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Card
        title={
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            <i className="fas fa-layer-group" style={{ marginRight: 8, color: "#3A3F63" }} />
            Workorder Wrappers
          </span>
        }
        extra={
          <Space>
            <Tooltip title="Refresh">
              <Button icon={<RedoOutlined />} onClick={fetchData} loading={loading} />
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(createPath)}
            >
              New Wrapper
            </Button>
          </Space>
        }
      >
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Search by title..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
          <Col span={5}>
            <Select
              placeholder="Filter by status"
              style={{ width: "100%" }}
              allowClear
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
            >
              <Option value="Open">Open</Option>
              <Option value="In Progress">In Progress</Option>
              <Option value="Completed">Completed</Option>
              <Option value="Cancelled">Cancelled</Option>
            </Select>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 1000 }}
          onChange={(_, __, sorter, extra) => {
            if (extra?.action === "sort" && sorter?.field) {
              const dir = sorter.order === "ascend" ? "" : "-";
              setOrdering(`${dir}${sorter.field}`);
              setPage(1);
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `Total ${t} records`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>
    </div>
  );
};

export default WorkorderWrapper;
